import { Link } from "react-router-dom";
import {
  getFormattedDate,
  getDaysFromNowDate,
  getFriendlyTimeFromNow,
  Tooltip,
  SkeletonLoader,
  convertCronToSchedulerData,
  convertCronToText,
  Label,
  LabelVariant,
} from "@SeemplicityDev/shared";
import { getNextSync } from "../../../Components/Schedule/ScheduleUtils";
import {
  dataSourceIcons,
  type DataSourceIcon,
} from "../../../icons/DatasourceIconsMap";
import { DateTime } from "luxon";

import {
  integrationCollectionStateEnum,
  PUBLIC_THREAT_INTELLIGENCE,
  ConnectionStatusEnum,
} from "@SeemplicityDev/shared/integrations";

import type {
  DataSourcesFastInfoResponse,
  DataSourcesSlowInfoResponse,
  DataSourceDefinitionsResponse,
} from "./types";

export const dataSourceStatusEnum = {
  COMING_SOON: "COMING_SOON",
  COMING_SOON_WIP: "COMING_SOON_WIP",
  ACTIVE_CS: "ACTIVE_CS",
  EARLY_ACCESS: "EARLY_ACCESS",
} as const;

export const STATUSES_TO_FILTER = ["ACTIVE_HIDDEN", "DELETED"];

export const isDatasourceDefinitionHidden = (datasourceDefinition?: {
  status?: string;
}): boolean => STATUSES_TO_FILTER.includes(datasourceDefinition?.status ?? "");

export const CATEGORY_ALL = "All";
export const LAST_COLLECTION_ALL = "All";

/** A single integrated instance of a data source definition, built up across fast/slow info passes. */
export interface DataSourceInstance {
  isEnabled?: boolean;
  name?: string;
  id?: string;
  rapId?: number | null;
  fieldsValues?: Record<string, unknown>;
  time?: unknown;
  nextSync?: string;
  collectTime?: string;
  lastSynced?: string | null;
  collectionState?: string | null;
  maxDaysNotSeenToCloseSetting?: number | null;
  lastCollectedThreshold?: number | null;
  lastReportedThreshold?: number | null;
  enableLastCollectedThreshold?: boolean;
  enableLastReportedThreshold?: boolean;
  connectionStatus?: string;
}

/** A data source definition enriched with instances and derived connection counts. */
export interface DataSourceDefinitionWithInstances {
  name?: string;
  imageLong?: string;
  imageShort?: string;
  unavailable?: boolean;
  earlyAccess?: boolean;
  isRapable?: boolean;
  enableCheckConnection?: boolean;
  category?: string;
  id?: string;
  instructions?: unknown;
  sections?: unknown;
  instances: DataSourceInstance[];
  enableLastCollectedThreshold?: boolean;
  enableLastReportedThreshold?: boolean;
  defaultLastCollectedThreshold?: number | null;
  defaultLastReportedThreshold?: number | null;
  hasAtLeastOneInstance?: boolean;
  isEnabled?: boolean;
  failedConnections?: number;
  succeededConnections?: number;
  disabledInstances?: number;
  rapInstancesCount?: number;
  nonRapInstancesCount?: number;
  collectionState?: string | null;
  connectionStatus?: string;
  failedInstanceId?: string;
  succeededInstanceId?: string;
  rapInstanceId?: string;
  lastSynced?: string | null;
}

/** Map of definition id -> enriched definition. */
export type DataSourceDefinitionsById = Record<
  string,
  DataSourceDefinitionWithInstances
>;

/** Map of category -> enriched definitions, produced by groupDataSourcesByCategory. */
export type DataSourcesByCategory = Record<
  string,
  DataSourceDefinitionWithInstances[]
>;

interface StatusOption {
  label: string;
  value: string | number;
}

/**
 * The last-collection filter input. The single-select SelectInput hands back a
 * single option object at runtime, while the DataSources state types it as an
 * array (empty when nothing is selected) — accept both and normalize.
 */
type LastCollectionValue =
  | { value?: string | number }
  | Array<{ value?: string | number }>
  | null;

interface FilterDataSourcesArgs {
  dataSources: DataSourcesByCategory;
  searchTerm: string;
  statuses: StatusOption[];
  lastCollection?: LastCollectionValue;
  category?: string;
}

export const filterDataSources = ({
  dataSources,
  searchTerm,
  statuses,
  lastCollection,
  category = CATEGORY_ALL,
}: FilterDataSourcesArgs): DataSourceDefinitionWithInstances[] => {
  const selectedLastCollection = Array.isArray(lastCollection)
    ? lastCollection[0]
    : lastCollection;
  return filterByCategory({
    category,
    dataSourcesByCategories: dataSources,
  })
    .filter(filterBySearchTerm(searchTerm))
    .filter(
      filterByLastCollection(
        selectedLastCollection?.value ?? LAST_COLLECTION_ALL
      )
    )
    .filter(filterByStatuses(statuses));
};

export const filterByLastCollection =
  (daysBack: string | number) =>
  (dataSource: DataSourceDefinitionWithInstances): boolean => {
    if (daysBack === LAST_COLLECTION_ALL) {
      return true;
    }
    const { lastSynced } = dataSource;

    if (!lastSynced) {
      return false;
    }

    const limit = getDaysFromNowDate(-Number(daysBack)).startOf("day");
    // Original JS compared a Date against a luxon DateTime; both coerce via
    // valueOf() at runtime. Cast the DateTime to Date for the comparison only —
    // runtime value and semantics are unchanged.
    return new Date(lastSynced) > (limit as unknown as Date);
  };

export const filterBySearchTerm =
  (searchTerm: string) =>
  (dataSource: DataSourceDefinitionWithInstances): boolean => {
    return !!dataSource?.name?.toLowerCase().includes(searchTerm.toLowerCase());
  };

interface FilterByCategoryArgs {
  category: string;
  dataSourcesByCategories: DataSourcesByCategory;
}

export const filterByCategory = ({
  category,
  dataSourcesByCategories,
}: FilterByCategoryArgs): DataSourceDefinitionWithInstances[] => {
  if (category === CATEGORY_ALL) {
    return Object.values(dataSourcesByCategories).flat();
  }

  return dataSourcesByCategories[category] || [];
};

export const filterByStatuses =
  (statuses: StatusOption[]) =>
  (dataSource: DataSourceDefinitionWithInstances): boolean => {
    const statusesValues = statuses.map(({ value }) => value);
    if (!statusesValues?.length) {
      return true;
    }

    const instanceStatuses: Array<string | number> = [];
    if (!dataSource.hasAtLeastOneInstance) {
      instanceStatuses.push(ConnectionStatusEnum.NOT_INTEGRATED);
    } else {
      if (dataSource?.instances?.some((instance) => instance?.isEnabled)) {
        instanceStatuses.push(ConnectionStatusEnum.INTEGRATED);
      }
    }

    if (dataSource.hasAtLeastOneInstance && !dataSource?.isEnabled) {
      instanceStatuses.push(ConnectionStatusEnum.DISABLED);
    }

    if (
      dataSource?.instances?.some(
        (instance) =>
          instance?.isEnabled &&
          instance.connectionStatus === ConnectionStatusEnum.FAILED
      )
    ) {
      instanceStatuses.push(ConnectionStatusEnum.FAILED);
    } else if (
      dataSource?.instances?.length &&
      !dataSource?.instances?.some(
        (instance) => instance.connectionStatus === ConnectionStatusEnum.FAILED
      ) &&
      dataSource?.instances?.some(
        (instance) => instance.connectionStatus === ConnectionStatusEnum.SUCCESS
      ) &&
      dataSource?.instances?.some((instance) => instance?.isEnabled)
    ) {
      instanceStatuses.push(ConnectionStatusEnum.SUCCESS);
    }

    let hasInstanceWithSelectedStatus = false;
    statusesValues.forEach((status) => {
      if (instanceStatuses?.includes(status)) {
        hasInstanceWithSelectedStatus = true;
      }
    });

    return hasInstanceWithSelectedStatus;
  };

export const instanceActions = {
  CREATE: "create",
  EDIT: "edit",
} as const;

export const LastCollectionEnum = {
  TODAY: 0,
  LAST_7_DAYS: 7,
  LAST_30_DAYS: 30,
  LAST_90_DAYS: 90,
} as const;

export const statusOptions: StatusOption[] = [
  { label: "Connected", value: ConnectionStatusEnum.SUCCESS },
  {
    label: "Connection Error",
    value: ConnectionStatusEnum.FAILED,
  },
  { label: "Integrated", value: ConnectionStatusEnum.INTEGRATED },
  { label: "Not Integrated", value: ConnectionStatusEnum.NOT_INTEGRATED },
  { label: "Integrated & Disabled", value: ConnectionStatusEnum.DISABLED },
];

export const lastCollectionOptions: StatusOption[] = [
  { label: "All", value: LAST_COLLECTION_ALL },
  { label: "Today", value: LastCollectionEnum.TODAY },
  {
    label: "Last 7 Days",
    value: LastCollectionEnum.LAST_7_DAYS,
  },
  { label: "Last 30 Days", value: LastCollectionEnum.LAST_30_DAYS },
  { label: "Last 90 Days", value: LastCollectionEnum.LAST_90_DAYS },
];

export const groupDataSourcesByCategory = (
  datasources: DataSourceDefinitionWithInstances[]
): DataSourcesByCategory => {
  const categories: DataSourcesByCategory = {};
  datasources.forEach((datasource) => {
    const category = datasource.category ?? "";
    if (!Object.keys(categories).includes(category)) {
      categories[category] = [datasource];
    } else {
      categories[category].push(datasource);
    }
  });

  return categories;
};

interface IntegrationTicketContentArgs {
  datasource: DataSourceDefinitionWithInstances;
  path: string;
  loading?: boolean;
}

export const getIntegrationTicketContent = ({
  datasource,
  path,
  loading = false,
}: IntegrationTicketContentArgs): JSX.Element => {
  if (datasource.earlyAccess) {
    return (
      <Label
        text="Early Access - Setup Only"
        variant={LabelVariant.Outlined}
        className="data-source-early-access-setup"
      />
    );
  }

  const connectionPart = getIntegrationTicketConnectionStatus({
    datasource,
    path,
    loading,
  });

  const lastSyncedPart = getIntegrationTicketLastSync({
    datasource,
    loading,
  });

  return (
    <>
      {connectionPart}
      {lastSyncedPart}
    </>
  );
};

interface DataSourceConnectionProps {
  datasource: DataSourceDefinitionWithInstances;
}

const DataSourceConnectionError: React.FC<DataSourceConnectionProps> = ({
  datasource,
}) => {
  return (
    <div className="data-source-status-wrap is-error">
      <div className="data-source-status-icon-wrap">
        <Tooltip type="info" content={`Connection Error`}>
          <i className={`data-source-status-icon seem-icon seem-icon-error`} />
        </Tooltip>
      </div>
      <div className={"data-source-status-text"}>
        {`Connection Error (${datasource?.failedConnections}/${(datasource?.instances?.length ?? 0) - (datasource.disabledInstances ?? 0)})`}
      </div>
    </div>
  );
};

const DatasourceConnectionSuccess: React.FC<DataSourceConnectionProps> = ({
  datasource,
}) => {
  if (datasource?.rapInstancesCount === 0) {
    return (
      <div className="data-source-status-wrap is-success">
        <div className="data-source-status-icon-wrap">
          <i className={"data-source-status-icon green-dot"} />
        </div>

        <div className={"data-source-status-text connection-success"}>
          {`Connected (${datasource.succeededConnections}/${datasource.succeededConnections})`}
        </div>
      </div>
    );
  }

  if (
    // all RAP
    (datasource?.rapInstancesCount ?? 0) > 0 &&
    datasource?.nonRapInstancesCount === 0
  ) {
    return (
      <div className="data-source-status-wrap is-rap">
        <div className={"data-source-status-text connection-unknown"}>
          {`Remote (${datasource.rapInstancesCount}/${datasource.rapInstancesCount})`}
        </div>
      </div>
    );
  }

  // mix of RAP and non-RAP
  return (
    <div className="data-source-status-wrap is-success">
      <div className="data-source-status-icon-wrap">
        <i className={"data-source-status-icon green-dot"} />
      </div>

      <div className={"data-source-status-text connection-success"}>
        {`Connected (${(datasource.succeededConnections ?? 0) + (datasource.rapInstancesCount ?? 0)}/${(datasource.succeededConnections ?? 0) + (datasource.rapInstancesCount ?? 0)})`}
      </div>
    </div>
  );
};

interface IntegrationTicketConnectionStatusArgs {
  datasource: DataSourceDefinitionWithInstances;
  path: string;
  loading?: boolean;
}

export const getIntegrationTicketConnectionStatus = ({
  datasource,
  path,
  loading,
}: IntegrationTicketConnectionStatusArgs): JSX.Element | null => {
  if (!datasource?.hasAtLeastOneInstance) {
    return null;
  }

  if (datasource.hasAtLeastOneInstance && !datasource?.isEnabled) {
    return (
      <div className={"data-source-status-wrap is-disabled"}>
        <div className="data-source-status-icon-wrap">
          <i className="data-source-status-icon seem-icon seem-icon-pause" />
        </div>
        <div className="data-source-status-text">Disabled</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={"data-source-status-wrap"}>
        <div className={"data-source-status-text"}>
          <SkeletonLoader height={"19px"} width={"120px"} />
        </div>
      </div>
    );
  }

  if (datasource?.category === PUBLIC_THREAT_INTELLIGENCE) {
    if ((datasource?.failedConnections ?? 0) > 0) {
      return <DataSourceConnectionError datasource={datasource} />;
    } else {
      return <DatasourceConnectionSuccess datasource={datasource} />;
    }
  }

  if ((datasource?.failedConnections ?? 0) > 0) {
    return (
      <Link
        to={`${path}/${
          datasource?.failedConnections === 1
            ? `${datasource?.id}/${datasource?.failedInstanceId}/Details`
            : datasource?.id
        }`}
      >
        <DataSourceConnectionError datasource={datasource} />
      </Link>
    );
  } else {
    return (
      <Link
        to={`${path}/${
          (datasource?.succeededConnections === 1 &&
            datasource?.nonRapInstancesCount === 0) ||
          (datasource?.rapInstancesCount === 1 &&
            datasource?.succeededConnections === 0)
            ? `${datasource?.id}/${datasource?.succeededInstanceId || datasource?.rapInstanceId}/Details`
            : datasource?.id
        }`}
      >
        <DatasourceConnectionSuccess datasource={datasource} />
      </Link>
    );
  }
};

interface IntegrationTicketLastSyncArgs {
  datasource: DataSourceDefinitionWithInstances;
  loading?: boolean;
}

export const getIntegrationTicketLastSync = ({
  datasource,
  loading,
}: IntegrationTicketLastSyncArgs): JSX.Element | null => {
  if (!datasource?.isEnabled) {
    return null;
  }

  return (
    <div className={`data-source-status-wrap`}>
      {loading ? (
        <SkeletonLoader height={"19px"} width={"180px"} />
      ) : (
        <>
          <div className={"integrations-ticket-def"}>Last Collection:</div>

          <div
            className={"integrations-ticket-value"}
            title={getFormattedDate(datasource.lastSynced ?? undefined)}
          >
            {!datasource?.lastSynced ? (
              <div className={"not-connected"}>N/A</div>
            ) : (
              getFriendlyTimeFromNow(datasource.lastSynced)
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface AddDataSourcesFastInfoArgs {
  datasourcesDefinitions: DataSourceDefinitionsById;
  datasourcesInstancesFastInfo: DataSourcesFastInfoResponse;
}

export const addDataSourcesFastInfo = ({
  datasourcesDefinitions,
  datasourcesInstancesFastInfo,
}: AddDataSourcesFastInfoArgs): DataSourceDefinitionsById => {
  const dataSourcesDefinitionsKeys = Object.keys(datasourcesDefinitions);
  datasourcesInstancesFastInfo.datasources?.edges?.forEach(
    (integratedDataSource) => {
      const dataSourceDefinitionKey = dataSourcesDefinitionsKeys.find(
        (key) => key === integratedDataSource?.node?.datasource_definition?.id
      );
      let dataSourceDefinitionObj:
        | DataSourceDefinitionWithInstances
        | undefined;
      if (dataSourceDefinitionKey) {
        dataSourceDefinitionObj =
          datasourcesDefinitions[dataSourceDefinitionKey];
      }

      if (dataSourceDefinitionObj && dataSourceDefinitionKey) {
        let parsedRequiredFieldsValue: Record<string, unknown>;
        let parsedSecretConfigurationValue: Record<string, unknown>;
        try {
          parsedRequiredFieldsValue = JSON.parse(
            integratedDataSource?.node?.required_fields_value as string
          );
          parsedSecretConfigurationValue = JSON.parse(
            integratedDataSource?.node?.secret_configuration_value as string
          );
        } catch (e) {
          parsedRequiredFieldsValue = {};
          parsedSecretConfigurationValue = {};
        }
        dataSourceDefinitionObj.instances.push({
          isEnabled: integratedDataSource?.node?.enabled,
          name: integratedDataSource?.node?.friendly_name,
          id: integratedDataSource?.node?.id,
          rapId: integratedDataSource?.node?.rap_instance_id,
          fieldsValues: {
            friendly_name: integratedDataSource?.node?.friendly_name,
            ...parsedRequiredFieldsValue,
            ...parsedSecretConfigurationValue,
          },
        });

        datasourcesDefinitions[dataSourceDefinitionKey] =
          dataSourceDefinitionObj;
      }
    }
  );

  dataSourcesDefinitionsKeys.forEach((dataSourceDefinitionId) => {
    const dataSourceDefinition = datasourcesDefinitions[dataSourceDefinitionId];
    let isEnabled = false;
    dataSourceDefinition.failedConnections = 0;
    dataSourceDefinition.succeededConnections = 0;
    dataSourceDefinition.disabledInstances = 0;
    dataSourceDefinition.instances.forEach((instance) => {
      if (instance?.isEnabled) {
        isEnabled = instance.isEnabled;
      }

      if (!instance?.isEnabled) {
        dataSourceDefinition.disabledInstances =
          (dataSourceDefinition.disabledInstances ?? 0) + 1;
      }
    });

    if (dataSourceDefinition.instances.length) {
      dataSourceDefinition.hasAtLeastOneInstance = true;
    }
    dataSourceDefinition.isEnabled = Boolean(isEnabled);
  });

  return datasourcesDefinitions;
};

interface AddDataSourcesSlowInfoArgs {
  datasourcesDefinitions: DataSourceDefinitionWithInstances[];
  datasourcesInstancesSlowInfo: DataSourcesSlowInfoResponse;
}

export const addDataSourcesSlowInfo = ({
  datasourcesDefinitions,
  datasourcesInstancesSlowInfo,
}: AddDataSourcesSlowInfoArgs): DataSourceDefinitionWithInstances[] => {
  datasourcesInstancesSlowInfo.datasources?.edges?.forEach(
    (integratedDataSource) => {
      const currentDataSourceDefinition = datasourcesDefinitions.find(
        (dsDefinition) =>
          dsDefinition.id ===
          integratedDataSource?.node?.datasource_definition?.id
      );

      if (currentDataSourceDefinition) {
        const currentInstanceIndex =
          currentDataSourceDefinition.instances.findIndex(
            (instance) => instance.id === integratedDataSource.node.id
          );

        if (currentInstanceIndex !== -1) {
          currentDataSourceDefinition.instances[currentInstanceIndex] = {
            ...currentDataSourceDefinition.instances[currentInstanceIndex],
            time:
              integratedDataSource?.node?.cron &&
              convertCronToSchedulerData({
                cron: integratedDataSource?.node?.cron,
                withOffset: true,
              }), // convert utc cron to Scheduler with local data
            nextSync: getNextSync(integratedDataSource?.node?.cron),
            collectTime: convertCronToText({
              cron: integratedDataSource?.node?.cron ?? undefined,
            }),
            lastSynced: integratedDataSource?.node?.last_collection_time,
            collectionState: integratedDataSource?.node?.collection_state,
            maxDaysNotSeenToCloseSetting:
              integratedDataSource?.node?.days_not_seen_to_declare_fixed,
            lastCollectedThreshold:
              integratedDataSource?.node?.last_collected_threshold,
            lastReportedThreshold:
              integratedDataSource?.node?.last_reported_threshold,
            enableLastCollectedThreshold:
              integratedDataSource?.node?.enable_last_collected_threshold,
            enableLastReportedThreshold:
              integratedDataSource?.node?.enable_last_reported_threshold,

            connectionStatus:
              integratedDataSource?.node?.check_connection_status,
          };
        }
      }

      datasourcesDefinitions.forEach((dataSourceDefinition) => {
        let latestSynced: string | null | undefined;
        dataSourceDefinition.failedConnections = 0;
        dataSourceDefinition.succeededConnections = 0;
        dataSourceDefinition.rapInstancesCount = 0;
        dataSourceDefinition.nonRapInstancesCount = 0;

        dataSourceDefinition.instances.forEach((instance) => {
          if (
            instance.collectionState === integrationCollectionStateEnum.RUNNING
          ) {
            dataSourceDefinition.collectionState =
              integrationCollectionStateEnum.RUNNING;
          }

          if (
            instance.connectionStatus === ConnectionStatusEnum.FAILED &&
            instance?.isEnabled
          ) {
            dataSourceDefinition.connectionStatus = ConnectionStatusEnum.FAILED;
            dataSourceDefinition.failedConnections =
              (dataSourceDefinition.failedConnections ?? 0) + 1;
            dataSourceDefinition.failedInstanceId = instance.id;
            dataSourceDefinition.nonRapInstancesCount =
              (dataSourceDefinition.nonRapInstancesCount ?? 0) + 1;
          }

          if (
            instance.connectionStatus === ConnectionStatusEnum.SUCCESS &&
            instance?.isEnabled
          ) {
            dataSourceDefinition.succeededConnections =
              (dataSourceDefinition.succeededConnections ?? 0) + 1;
            dataSourceDefinition.succeededInstanceId = instance.id;
            dataSourceDefinition.nonRapInstancesCount =
              (dataSourceDefinition.nonRapInstancesCount ?? 0) + 1;
          }

          if (
            !instance.connectionStatus &&
            instance?.isEnabled &&
            instance.rapId !== null
          ) {
            dataSourceDefinition.rapInstancesCount =
              (dataSourceDefinition.rapInstancesCount ?? 0) + 1;
            dataSourceDefinition.rapInstanceId = instance.id;
          }

          if (!latestSynced && !!instance.lastSynced) {
            latestSynced = instance.lastSynced;
          } else if (
            latestSynced &&
            DateTime.fromISO(instance.lastSynced ?? "") >=
              DateTime.fromISO(latestSynced)
          ) {
            latestSynced = instance.lastSynced;
          }
          dataSourceDefinition.lastSynced = latestSynced;
        });
      });
    }
  );
  return datasourcesDefinitions;
};

interface MergeDefinitionsWithFastInfoArgs {
  dataSourceDefinitionsData?: DataSourceDefinitionsResponse;
  integratedDataSourcesFastInfo: DataSourcesFastInfoResponse;
  errorLogger: (error: unknown, message: string) => void;
  currentTheme?: string;
}

export const mergeDefinitionsWithFastInfo = ({
  dataSourceDefinitionsData,
  integratedDataSourcesFastInfo,
  errorLogger,
  currentTheme = "LIGHT",
}: MergeDefinitionsWithFastInfoArgs): DataSourcesByCategory => {
  let dataSourcesDefinitionsWithInstances: DataSourceDefinitionsById = {};
  dataSourceDefinitionsData?.datasource_definitions?.edges?.forEach(
    (dataSourceDefinition) => {
      // dataSourceIcons is keyed by "LIGHT"/"DARK"; the original JS also reads an
      // optional `name` off the resolved icon (undefined at runtime when absent).
      const datasourceIcon = dataSourceIcons[
        dataSourceDefinition.node?.name as string
      ]?.[currentTheme as keyof (typeof dataSourceIcons)[string]] as
        | (DataSourceIcon & { name?: string })
        | null
        | undefined;
      if (dataSourceDefinition?.node?.name !== "Manual") {
        const name = datasourceIcon?.name
          ? datasourceIcon?.name
          : dataSourceDefinition.node.name;
        const imageLong = datasourceIcon?.L;
        if (!imageLong) {
          errorLogger(
            null,
            `long version logo for ${name} is missing in datasources page.\nname: ${dataSourceDefinition.node?.name}.`
          );
        }
        const imageShort = datasourceIcon?.S;
        if (!imageShort) {
          errorLogger(
            null,
            `short version logo for ${name} is missing in datasources page.\nname: ${dataSourceDefinition.node?.name}.`
          );
        }

        const unavailable =
          dataSourceDefinition.node.status ===
            dataSourceStatusEnum.COMING_SOON ||
          dataSourceDefinition.node.status ===
            dataSourceStatusEnum.COMING_SOON_WIP ||
          dataSourceDefinition.node.status === dataSourceStatusEnum.ACTIVE_CS;
        const earlyAccess =
          dataSourceDefinition.node.status ===
          dataSourceStatusEnum.EARLY_ACCESS;

        dataSourcesDefinitionsWithInstances[
          dataSourceDefinition.node?.id as string
        ] = {
          name,
          imageLong,
          imageShort,
          unavailable,
          earlyAccess,
          isRapable: dataSourceDefinition.node?.is_rapable as boolean,
          enableCheckConnection: dataSourceDefinition.node
            ?.enable_check_connection as boolean,
          category: dataSourceDefinition.node?.category,
          id: dataSourceDefinition.node?.id,
          instructions: dataSourceDefinition.node?.instructions,
          sections: dataSourceDefinition.node?.sections,
          instances: [],
          enableLastCollectedThreshold: dataSourceDefinition.node
            ?.enable_last_collected_threshold as boolean,
          enableLastReportedThreshold: dataSourceDefinition.node
            ?.enable_last_reported_threshold as boolean,

          defaultLastCollectedThreshold: dataSourceDefinition.node
            ?.default_last_collected_threshold as number | null,
          defaultLastReportedThreshold: dataSourceDefinition.node
            ?.default_last_reported_threshold as number | null,
        };
      }
    }
  );

  dataSourcesDefinitionsWithInstances = addDataSourcesFastInfo({
    datasourcesDefinitions: dataSourcesDefinitionsWithInstances,
    datasourcesInstancesFastInfo: integratedDataSourcesFastInfo,
  });

  const dataSourcesDefinitionsWithInstancesArray = Object.keys(
    dataSourcesDefinitionsWithInstances
  ).map(
    (dataSourceDefinitionId) =>
      dataSourcesDefinitionsWithInstances[dataSourceDefinitionId]
  );

  const groupedByCategoryDataSourcesToShow = groupDataSourcesByCategory(
    dataSourcesDefinitionsWithInstancesArray || []
  );
  return groupedByCategoryDataSourcesToShow;
};
